// src/pages/ModelConfig.tsx
import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import { Switch, Input, Button, Collapse, Card, List, Tag, Tooltip, Modal, Form, message, Select, Space } from 'antd';
import { EyeInvisibleOutlined, EditOutlined, DeleteOutlined, PlusOutlined, CheckCircleTwoTone, SearchOutlined } from '@ant-design/icons';
import {
  fetchUserWorkspaceLLMConfig,
  createLLMPlatform,
  updateLLMPlatform,
  createLLMGroup,
  updateLLMGroup,
  createLLMModel,
  updateLLMModel,
  deleteLLMModel,
  deleteLLMGroup,
  updateLLMUserPlatformConfig,
  updateLLMUserModelConfig,
  addUserCustomModel,
  addUserCustomGroup,
  deleteUserCustomModel,
  deleteUserCustomGroup,
  GroupDetailForUserUI,
  LLMUserGroupConfig,
  createLLMUserPlatformConfig,
} from '../api/llm_config'; // API functions will be updated later
import { useTranslation } from 'react-i18next';
const { Panel } = Collapse;

// New Type Definitions based on backend schemas
interface LLMModel {
  id: number;
  name: string;
  model_id_name: string; // Corresponds to llm_model_id in old, now model_id_name
  status: boolean;
  is_default: boolean; // User specific setting
  is_custom: boolean; // Indicates if it's a user-added model
  user_model_config_id?: number; // To store the id of the user's config for this model
}

interface LLMGroup {
  id: number;
  name: string;
  models: LLMModel[];
  is_custom: boolean; // Indicates if it's a user-added group
}

interface LLMPlatform {
  id: number;
  name: string;
  provider_type: string;
  api_key: string; // User specific setting
  api_base_url: string; // User specific setting
  status: boolean; // User specific setting for the platform
  groups: LLMGroup[];
  user_platform_config_id?: number; // To store the id of the user's config for this platform
}

export interface FetchUserWorkspaceLLMConfigResponse {
  data: {
    platforms: LLMPlatform[];
  };
}

const PROVIDER_TYPES = [
  'OpenAI',
  'OpenAI-Response',
  'Gemini',
  'Anthropic',
  'Azure OpenAI',
  'Ollama',
  'DeepSeek',
  'Open Router',
  // Add other provider types as needed
];

// 新增：后端数据转换为前端结构
const convertBackendToFrontend = (platformsFromBackend: any[]): LLMPlatform[] => {
  return platformsFromBackend.map((p: any) => ({
    id: p.id,
    name: p.name,
    provider_type: p.provider_type,
    api_key: p.user_api_key_set ? '******' : '', // 只做占位，实际编辑时再拉取
    api_base_url: p.user_api_url_override || p.default_api_url || '',
    status: p.is_enabled_by_user_for_platform,
    is_custom: p.is_custom,
    user_platform_config_id: p.user_platform_config_id,
    groups: (p.groups || []).map((g: any) => ({
      id: g.id,
      name: g.name,
      is_custom: g.is_custom,
      models: (g.models || []).map((m: any) => ({
        id: m.id,
        name: m.name,
        model_id_name: m.model_id_str,
        status: m.is_enabled_by_user,
        is_default: m.is_default_for_user,
        is_custom: m.is_custom,
        user_model_config_id: m.user_config_id,
      }))
    }))
  }));
};

const ModelConfig: React.FC = () => {
  const { t } = useTranslation();
  const [allPlatforms, setAllPlatforms] = useState<LLMPlatform[]>([]); // Holds all platforms (default + user-custom)
  const [displayedPlatforms, setDisplayedPlatforms] = useState<LLMPlatform[]>([]); // Platforms to display after search
  const [selectedPlatform, setSelectedPlatform] = useState<LLMPlatform | null>(null);
  const [apiEdit, setApiEdit] = useState(false);
  const [addModelModalOpen, setAddModelModalOpen] = useState(false);
  const [addModelForm] = Form.useForm();
  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
  const [addGroupForm] = Form.useForm();
  const [addPlatformModalOpen, setAddPlatformModalOpen] = useState(false);
  const [editModelModalOpen, setEditModelModalOpen] = useState(false);
  const [editModelForm] = Form.useForm();
  const [editingModel, setEditingModel] = useState<LLMModel | null>(null);
  const [editingGroup, setEditingGroup] = useState<LLMGroup | null>(null); // For context when adding/editing model
  const [groupNameManuallyEdited, setGroupNameManuallyEdited] = useState(false);
  const [platformForm] = Form.useForm();
  const selectedPlatformIdRef = React.useRef<number | null>(null);
  const [activeGroupKeys, setActiveGroupKeys] = useState<string[]>([]);

  const userId = Number(localStorage.getItem('user_id'));
  const workspaceId = Number(localStorage.getItem('workspace_id'));

  const loadUserConfig = useCallback(async () => {
    if (!userId || !workspaceId) {
      message.error(t('modelConfig.missingUserOrWorkspace'));
      return;
    }
    try {
      const res = await fetchUserWorkspaceLLMConfig(workspaceId, userId);
      const data = convertBackendToFrontend((res.data as { platforms: any[] }).platforms);
      setAllPlatforms(data);
      setDisplayedPlatforms(data);
      if (data.length > 0) {
        let toSelect: LLMPlatform | undefined;
        if (selectedPlatformIdRef.current) {
          toSelect = data.find(p => p.id === selectedPlatformIdRef.current);
        }
        setSelectedPlatform(toSelect || data[0]);
      } else {
        setSelectedPlatform(null);
      }
    } catch (error) {
      message.error(t('modelConfig.loadConfigError'));
      console.error("Error loading user config:", error);
    }
  }, [userId, workspaceId, t]);

  useEffect(() => {
    loadUserConfig();
  }, [loadUserConfig]);

  useEffect(() => {
    if (selectedPlatform && selectedPlatform.groups) {
      setActiveGroupKeys(selectedPlatform.groups.map(g => g.id.toString()));
    }
  }, [selectedPlatform]);

  const handlePlatformChange = (platform: LLMPlatform) => {
    setSelectedPlatform(platform);
    selectedPlatformIdRef.current = platform.id;
    setApiEdit(false);
  };

  const handleSaveApiInfo = async () => {
    console.log(selectedPlatform)
    if (!selectedPlatform) return;
    try {
      if (selectedPlatform.user_platform_config_id) {
        await updateLLMUserPlatformConfig(selectedPlatform.user_platform_config_id, {
          api_key: selectedPlatform.api_key,
          api_url_override: selectedPlatform.api_base_url,
          is_enabled: selectedPlatform.status,
        });
      } else {
        // 新增用户平台配置
        await createLLMUserPlatformConfig({
          user_id: userId,
          workspace_id: workspaceId,
          platform_id: selectedPlatform.id,
          api_key: selectedPlatform.api_key,
          api_url_override: selectedPlatform.api_base_url,
          is_enabled: selectedPlatform.status,
        });
      }
      message.success(t('modelConfig.updateSuccess'));
      setApiEdit(false);
      loadUserConfig();
    } catch (error) {
      message.error(t('modelConfig.updateError'));
    }
  };

  const handlePlatformUserStatusChange = async (checked: boolean) => {
    if (!selectedPlatform || !selectedPlatform.user_platform_config_id) return;
    try {
      await updateLLMUserPlatformConfig(selectedPlatform.user_platform_config_id, {
        is_enabled: checked,
        api_key: selectedPlatform.api_key,
        api_url_override: selectedPlatform.api_base_url,
      });
      message.success(t('modelConfig.statusUpdated'));
      setSelectedPlatform(prev => prev ? { ...prev, status: checked } : null);
      loadUserConfig();
    } catch (error) {
      message.error(t('modelConfig.statusUpdateError'));
    }
  };

  const fetchGroups = async (platformId: number) => {
    const res = await fetch(`/api/v1/llm_model_config/platforms/${platformId}/groups/?user_id=${userId}&workspace_id=${workspaceId}`);
    const data: GroupDetailForUserUI[] = await res.json();
  };

  const handleModelIdNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    addModelForm.setFieldsValue({ modelIdName: value });
    if (!groupNameManuallyEdited) {
      let groupName = value;
      if (typeof groupName === 'string' && groupName.includes('/')) {
        groupName = groupName.split('/')[0];
      }
      addModelForm.setFieldsValue({ groupName });
    }
  };

  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGroupNameManuallyEdited(true);
  };

  const handleAddModel = async (values: any) => {
    if (!selectedPlatform) {
      message.error(t('modelConfig.selectPlatform'));
      return;
    }
    // groupName 直接取表单值
    const groupName = values.groupName;
    try {
      await addUserCustomModel(userId, workspaceId, {
        platform_id: selectedPlatform.id,
        group_name: groupName,
        model_name: values.modelName,
        model_id_str: values.modelIdName,
        icon: values.icon,
        tags: values.tags,
        is_free: values.isFree,
        is_enabled_by_user: true,
        is_default_for_user: values.isDefault,
      });
      message.success(t('modelConfig.addModelSuccess'));
      setAddModelModalOpen(false);
      addModelForm.resetFields();
      loadUserConfig();
    } catch (error) {
      message.error(t('modelConfig.addModelError'));
    }
  };

  const handleAddGroup = async (values: any) => {
    if (!selectedPlatform) {
      message.error(t('modelConfig.selectPlatform'));
      return;
    }
    try {
      await addUserCustomGroup(userId, workspaceId, {
        platform_id: selectedPlatform.id,
        group_name: values.groupName,
      });
      message.success(t('modelConfig.addGroupSuccess'));
      setAddGroupModalOpen(false);
      addGroupForm.resetFields();
      loadUserConfig();
    } catch (error) {
      message.error(t('modelConfig.addGroupError'));
    }
  };

  const handleAddPlatform = async (values: any) => {
    try {
      await createLLMPlatform({
        name: values.providerName,
        provider_type: values.providerType,
        // Default platforms are typically not user-specific initially for api_key/url
      });
      message.success(t('modelConfig.addPlatformSuccess'));
      setAddPlatformModalOpen(false);
      platformForm.resetFields();
      loadUserConfig(); // Reload all platforms
    } catch (error) {
      message.error(t('modelConfig.addPlatformError'));
    }
  };

  const openAddModelModal = () => {
    addModelForm.resetFields();
    setGroupNameManuallyEdited(false);
    setAddModelModalOpen(true);
  };

  const openAddGroupModal = () => {
    addGroupForm.resetFields();
    setAddGroupModalOpen(true);
  };

  const handleEditModel = (model: LLMModel, group: LLMGroup) => {
    setEditingModel(model);
    setEditingGroup(group);
    editModelForm.setFieldsValue({
      modelName: model.name,
      modelIdName: model.model_id_name,
      status: model.status,
      isDefault: model.is_default,
    });
    setEditModelModalOpen(true);
  };

  const handleEditModelOk = async () => {
    if (!editingModel || !selectedPlatform || !editingGroup) return;
    const values = await editModelForm.validateFields();
    try {
        // 更新自定义模型本体
        await updateLLMModel(editingModel.id, {
          name: values.modelName,
          model_id_str: values.modelIdName,
        });
        // 更新用户配置
        if (editingModel.user_model_config_id) {
          await updateLLMUserModelConfig(editingModel.user_model_config_id, {
            is_enabled_by_user: values.status,
            is_default_for_user: values.isDefault,
          });
        }
      
      message.success(t('modelConfig.updateSuccess'));
      setEditModelModalOpen(false);
      loadUserConfig();
    } catch (error) {
      message.error(t('modelConfig.updateError'));
    }
  };

  const handleDeleteModelOrConfig = async (model: LLMModel) => {
    if (!selectedPlatform) return;
    Modal.confirm({
      title: t('modelConfig.deleteConfirm', { name: model.name }),
      onOk: async () => {
        try {
          if (model.is_custom) {
            await deleteUserCustomModel(model.id, userId, workspaceId); // model.id is the custom model's ID
          } else if (model.user_model_config_id) {
            // 系统模型和他人模型只做软删除
            await updateLLMUserModelConfig(model.user_model_config_id, {
              is_enabled_by_user: false, // 用户级隐藏
              is_default_for_user: false,
            });
          }
          message.success(t('modelConfig.deleteSuccess'));
          loadUserConfig();
        } catch (error) {
          message.error(t('modelConfig.deleteError'));
        }
      }
    });
  };

  const handleDeleteGroup = async (group: LLMGroup) => {
    if (!selectedPlatform) return;
    Modal.confirm({
      title: t('modelConfig.deleteGroupConfirm', { name: group.name }),
      content: group.models.length > 0 ? t('modelConfig.deleteGroupWarning') : '',
      onOk: async () => {
        try {
          if (group.is_custom) {
            await deleteUserCustomGroup(group.id, userId, workspaceId);
          } else {
            // Deleting a default group might not be allowed or might have different implications.
            // For now, we assume only custom groups can be deleted this way.
            message.warning(t('modelConfig.cannotDeleteDefaultGroup'));
            return;
          }
          message.success(t('modelConfig.deleteGroupSuccess'));
          loadUserConfig();
        } catch (error) {
          message.error(t('modelConfig.deleteGroupError'));
        }
      }
    });
  };

  const handleSearchPlatform = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    const filtered = allPlatforms.filter(p => p.name.toLowerCase().includes(searchValue));
    setDisplayedPlatforms(filtered);
  };

  // Update selectedPlatform's api_key or api_base_url directly for controlled components
  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedPlatform) {
      setSelectedPlatform({ ...selectedPlatform, api_key: e.target.value });
    }
  };

  const handleApiUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedPlatform) {
      setSelectedPlatform({ ...selectedPlatform, api_base_url: e.target.value });
    }
  };

  return (
    <div>
      <Header />
      <div style={{ padding: 20, paddingTop: 84 }}>
        <h2>{t('modelConfig.title')}</h2>
        <div style={{ display: 'flex', minHeight: 'calc(100vh - 84px)' }}>
          <div style={{ width: 280, borderRight: '1px solid #f0f0f0', paddingRight: 20, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
              <h3>{t('modelConfig.platforms')}</h3>
              <Button size="small" type="primary" onClick={() => setAddPlatformModalOpen(true)} icon={<PlusOutlined />} />
            </div>
            <Input
              placeholder={t('modelConfig.searchPlatform')}
              prefix={<SearchOutlined />}
              onChange={handleSearchPlatform}
              style={{ marginBottom: 10 }}
            />
            <List
              itemLayout="horizontal"
              dataSource={displayedPlatforms} // Use displayedPlatforms for searchability
              renderItem={item => (
                <List.Item
                  onClick={() => handlePlatformChange(item)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor: selectedPlatform?.id === item.id ? '#e6f7ff' : 'transparent',
                    borderLeft: selectedPlatform?.id === item.id ? '3px solid #1890ff' : '3px solid transparent',
                    borderRadius: '4px',
                    marginBottom: '4px',
                  }}
                  actions={[
                    <Switch
                      key={`switch-${item.id}`}
                      checked={item.status} // Platform status from user config
                      onChange={(checked) => {
                        // Need to select the platform first if not already selected to make the switch work correctly
                        if (selectedPlatform?.id !== item.id) {
                          setSelectedPlatform(item);
                        }
                        // Directly call status change after ensuring platform is selected
                        // This might require a slight refactor if selectedPlatform isn't updated immediately
                        // For now, assume it updates or handlePlatformUserStatusChange can take platform_id
                        handlePlatformUserStatusChange(checked); // This function needs to be aware of which platform it's for
                      }}
                      size="small"
                    />,
                  ]}
                >
                  <List.Item.Meta
                     // avatar={<Avatar src={item.icon_url || default_icon_url} />} // Assuming platform might have an icon
                    title={<span style={{ fontWeight: selectedPlatform?.id === item.id ? 600 : 400 }}>{item.name}</span>}
                  />
                </List.Item>
              )}
              style={{ flexGrow: 1, overflowY: 'auto' }}
            />
          </div>
          <div style={{ flex: 1, paddingLeft: 20 }}>
            {selectedPlatform && (
              <div>
                <Card 
                  title={selectedPlatform.name}
                  extra={apiEdit ? (
                    <Space>
                      <Button type="primary" onClick={handleSaveApiInfo}>{t('modelConfig.save')}</Button>
                      <Button onClick={() => { setApiEdit(false); loadUserConfig(); }}>{t('modelConfig.cancel')}</Button>
                    </Space>
                  ) : (
                    <Button icon={<EditOutlined />} onClick={() => setApiEdit(true)}>{t('modelConfig.editApi')}</Button>
                  )}
                >
                  <Form layout="vertical">
                    <Form.Item label={t('modelConfig.apiKey')}>
                      <Input.Password
                        value={selectedPlatform.api_key}
                        onChange={handleApiKeyChange}
                        disabled={!apiEdit}
                        placeholder={t('modelConfig.apiKeyPlaceholder')}
                        iconRender={visible => (visible ? <EyeInvisibleOutlined /> : <EyeInvisibleOutlined />)}
                      />
                    </Form.Item>
                    <Form.Item label={t('modelConfig.apiBaseUrl')}>
                      <Input
                        value={selectedPlatform.api_base_url}
                        onChange={handleApiUrlChange}
                        disabled={!apiEdit}
                        placeholder={t('modelConfig.apiBaseUrlPlaceholder')}
                      />
                    </Form.Item>
                     {/* Provider Type display - not editable by user for existing platforms */}
                    <Form.Item label={t('modelConfig.providerType')}>
                        <Input value={selectedPlatform.provider_type} disabled />
                    </Form.Item>
                  </Form>
                </Card>

                <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>{t('modelConfig.models')}</h3>
                  <Button type="dashed" onClick={openAddModelModal} icon={<PlusOutlined />}>{t('modelConfig.addModel')}</Button>
                </div>

                {selectedPlatform.groups && selectedPlatform.groups.length > 0 ? (
                  <Collapse
                    accordion={false}
                    activeKey={activeGroupKeys}
                    onChange={keys => setActiveGroupKeys(Array.isArray(keys) ? keys : [keys])}
                    style={{ marginTop: 10 }}
                  >
                    {selectedPlatform.groups.map(group => (
                      <Panel 
                        header={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <span>{group.name} {/*group.is_custom && <Tag color="blue">{t('modelConfig.custom')}</Tag>*/}</span>
                            <Space>
                              {/* <Tooltip title={t('modelConfig.addModelToGroup')}>
                                <Button size="small" type="text" icon={<PlusOutlined />} onClick={(e) => { e.stopPropagation(); openAddModelModal(); }} />
                              </Tooltip> */}
                              {/* {group.is_custom && (
                                <Tooltip title={t('modelConfig.deleteGroup')}>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group); }} />
                                </Tooltip>
                              )} */}
                            </Space>
                          </div>
                        }
                        key={group.id.toString()}
                      >
                        <List
                          itemLayout="horizontal"
                          dataSource={group.models}
                          renderItem={model => (
                            <List.Item
                              actions={[
                                <Tooltip title={t('modelConfig.editModel')}>
                                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleEditModel(model, group)} />
                                </Tooltip>,
                                <Tooltip title={ t('modelConfig.deleteModel') }>
                                  <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteModelOrConfig(model)} />
                                </Tooltip>,
                                // <Switch
                                //   key={`model-switch-${model.id}`}
                                //   checked={model.status}
                                //   onChange={async (checked) => {
                                //     if (model.user_model_config_id) {
                                //       try {
                                //         await updateLLMUserModelConfig(model.user_model_config_id, {
                                //           is_enabled_by_user: checked,
                                //           is_default_for_user: model.is_default,
                                //         });
                                //         message.success(t('modelConfig.statusUpdated'));
                                //         loadUserConfig(); // Reload to reflect changes
                                //       } catch (err) {
                                //         message.error(t('modelConfig.statusUpdateError'));
                                //       }
                                //     } else {
                                //       // This might happen for a default model the user hasn't interacted with yet.
                                //       // Ideally, a user_model_config should be created on first interaction or pre-created.
                                //       // For now, log a warning or disable switch if no config_id.
                                //       message.warning(t('modelConfig.noUserConfigForSwitch'));
                                //     }
                                //   }}
                                //   size="small"
                                // />,
                                <Tooltip title={t('modelConfig.setDefaultTooltip')}>
                                  <CheckCircleTwoTone 
                                    twoToneColor={model.is_default ? "#52c41a" : "#ccc"} 
                                    onClick={async () => {
                                      if (model.user_model_config_id) {
                                        try {
                                          await updateLLMUserModelConfig(model.user_model_config_id, {
                                            is_enabled_by_user: model.status,
                                            is_default_for_user: !model.is_default,
                                          });
                                          message.success(t('modelConfig.defaultStatusUpdated'));
                                          loadUserConfig();
                                        } catch (err) {
                                          message.error(t('modelConfig.defaultStatusUpdateError'));
                                        }
                                      } else {
                                        message.warning(t('modelConfig.noUserConfigForDefault'));
                                      }
                                    }}
                                    style={{ cursor: 'pointer', fontSize: '16px', marginLeft: '8px' }}
                                  />
                                </Tooltip>
                              ]}
                            >
                              <List.Item.Meta
                                title={<span>{model.name} {model.is_custom && <Tag color="purple">{t('modelConfig.custom')}</Tag>}</span>}
                                description={`${t('modelConfig.modelId')}: ${model.model_id_name}`}
                              />
                            </List.Item>
                          )}
                        />
                      </Panel>
                    ))}
                  </Collapse>
                ) : (
                  <p>{t('modelConfig.noGroupsInPlatform')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modals */}
      <Modal
        title={t('modelConfig.addPlatform')}
        visible={addPlatformModalOpen}
        onOk={() => platformForm.submit()}
        onCancel={() => setAddPlatformModalOpen(false)}
        destroyOnClose
      >
        <Form form={platformForm} layout="vertical" onFinish={handleAddPlatform}>
          <Form.Item name="providerName" label={t('modelConfig.platformName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="providerType" label={t('modelConfig.providerType')} rules={[{ required: true }]}>
            <Select placeholder={t('modelConfig.selectProviderType')}>
              {PROVIDER_TYPES.map(type => <Select.Option key={type} value={type}>{type}</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('modelConfig.addGroupToPlatform', { platformName: selectedPlatform?.name })}
        visible={addGroupModalOpen}
        onOk={() => addGroupForm.submit()}
        onCancel={() => setAddGroupModalOpen(false)}
        destroyOnClose
      >
        <Form form={addGroupForm} layout="vertical" onFinish={handleAddGroup}>
          <Form.Item name="groupName" label={t('modelConfig.groupName')} rules={[{ required: true, message: t('modelConfig.groupNameRequired') }]}>
            <Input placeholder={t('modelConfig.groupNamePlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('modelConfig.addModelToGroupNamed')}
        visible={addModelModalOpen}
        onOk={() => addModelForm.submit()}
        onCancel={() => setAddModelModalOpen(false)}
        destroyOnClose
      >
        <Form form={addModelForm} layout="vertical" onFinish={handleAddModel}>
          <Form.Item name="modelName" label={t('modelConfig.modelName')} rules={[{ required: true }]}> 
            <Input />
          </Form.Item>
          <Form.Item name="modelIdName" label={t('modelConfig.modelIdName')} rules={[{ required: true }]}> 
            <Input onChange={handleModelIdNameChange} />
          </Form.Item>
          <Form.Item name="groupName" label={t('modelConfig.groupName')} rules={[{ required: true, message: t('modelConfig.groupNameRequired') }]}> 
            <Input onChange={handleGroupNameChange} />
          </Form.Item>
          {/* <Form.Item name="icon" label={t('modelConfig.icon')}>
            <Input placeholder={t('modelConfig.iconPlaceholder')} />
          </Form.Item>
          <Form.Item name="tags" label={t('modelConfig.tags')}>
            <Select mode="tags" style={{ width: '100%' }} placeholder={t('modelConfig.tagsPlaceholder')} />
          </Form.Item>
          <Form.Item name="isFree" label={t('modelConfig.isFree')} valuePropName="checked">
            <Switch />
          </Form.Item> */}
          {/* <Form.Item name="status" label={t('modelConfig.status')} valuePropName="checked">
            <Switch />
          </Form.Item> */}
          <Form.Item name="isDefault" label={t('modelConfig.setDefault')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('modelConfig.editModelNamed', { modelName: editingModel?.name })}
        visible={editModelModalOpen}
        onOk={handleEditModelOk}
        onCancel={() => setEditModelModalOpen(false)}
        destroyOnClose
      >
        <Form form={editModelForm} layout="vertical">
          <Form.Item name="modelName" label={t('modelConfig.modelName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="modelIdName" label={t('modelConfig.modelIdName')} rules={[{ required: true }]}>
            <Input disabled={!editingModel?.is_custom} />
          </Form.Item>
          {/* <Form.Item name="status" label={t('modelConfig.status')} valuePropName="checked">
            <Switch />
          </Form.Item> */}
          <Form.Item name="isDefault" label={t('modelConfig.setDefault')} valuePropName="checked">
            <Switch />
          </Form.Item>
          {/* <Form.Item name="icon" label={t('modelConfig.icon')}>
            <Input />
          </Form.Item>
          <Form.Item name="tags" label={t('modelConfig.tags')}>
            <Select mode="tags" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isFree" label={t('modelConfig.isFree')} valuePropName="checked">
            <Switch />
          </Form.Item> */}
        </Form>
      </Modal>
    </div>
  );
};

export default ModelConfig;
